import {
  Button,
  Container,
  Drawer,
  Page,
  TextBalancer,
} from 'components/common';
import HeaderSEO from 'components/HeaderSEO';
import {
  api as productsApi,
  makeProductsCacheKey,
  Product,
  useProducts,
} from 'lib/product';
import {
  api as productCategoryApi,
  ProductCategory,
  SpecialProductCategory,
} from 'lib/productCategory';
import {
  CollectionMenu,
  ProductCategoriesSidebar,
  ProductCategoriesTabs,
  ProductsGrid,
  TagsFilter,
} from 'lib/sellingProduct';
import { GetServerSideProps } from 'next';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useState } from 'react';
import * as analytics from 'lib/analytics';
import { getSession } from 'next-auth/client';
import { redirectToLoginPage, useAuth } from 'lib/auth';
import { useUser } from 'lib/account';
import { useAuthStatus } from 'lib/auth/AuthStatusContext';

interface ServerSideProps {
  fallback: {
    products: {
      [key: string]: Product[];
    };
  };
  category: ProductCategory | null;
  categories: ProductCategory[];
  collections: CollectionMenu[];
  specialProductCategories: SpecialProductCategory[];
}

const PAGE_SIZE = 9;

const ProductsPage = ({
  fallback,
  category,
  categories,
  collections,
  specialProductCategories,
}: ServerSideProps) => {
  const { query, push, pathname } = useRouter();
  const { category_ids, tag_ids, collection_ids, search } = query;
  const [
    showProductFiltersDrawer,
    setShowProductFiltersDrawer,
  ] = useState(false);
  const key = makeProductsCacheKey({
    ...query,
    sort_field: 'published_at',
  });

  const { loading: isLoadingAuth, session } = useAuth();
  const { user, isLoading: isLoadingUser } = useUser(
    session?.user?.userId,
  );

  const { authStatus } = useAuthStatus();

  const isGuest = useMemo(() => !session, [session]);

  const isTieredUser = useMemo(
    () => (user && user.is_tiered) ?? false,
    [user],
  );

  const isSpecialCategoryActive = useMemo(() => {
    const specialCategoryIds = Array.from(
      new Set(
        specialProductCategories.reduce<string[]>((acc, category) => {
          const ids = category.id
            .split(',')
            .map((id) => id.trim())
            .filter(Boolean);

          return acc.concat(ids);
        }, []),
      ),
    );

    const categoryIds = Array.isArray(category_ids)
      ? category_ids
      : typeof category_ids === 'string'
      ? category_ids
          .split(',')
          .map((id) => id.trim())
          .filter(Boolean)
      : [];

    return categoryIds.some((id) => specialCategoryIds.includes(id));
  }, [category_ids, specialProductCategories]);

  const fallbackData = key ? fallback?.products?.[key] : [];

  const {
    products,
    isLoading: isLoadingProducts,
    mutate,
  } = useProducts(
    {
      query: {
        category_ids,
        tag_ids,
        collection_ids,
        search,
        sort_field: 'published_at',
      },
      fallbackData,
    },
    !isLoadingAuth,
  );

  const [loadedCount, setLoadedCount] = useState(0);
  const [isLoadingMoreProducts, setIsLoadingMoreProducts] = useState(
    false,
  );

  useEffect(() => {
    const initialLoadedCount =
      products.length > PAGE_SIZE ? PAGE_SIZE : products.length;

    setLoadedCount(initialLoadedCount);

    if (products.length) {
      analytics.viewProducts({ category, products });
    }
  }, [category, products]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => mutate(), [authStatus]);

  const setQueryParams = (queryParams) => {
    push(
      {
        pathname,
        query: queryParams,
      },
      // @ts-ignore
      null,
      { shallow: true },
    );
  };

  const specialProductTitle = useMemo(():
    | string
    | React.ReactNode
    | null => {
    if (
      !isSpecialCategoryActive ||
      isLoadingAuth ||
      isLoadingProducts
    )
      return null;

    if (isGuest)
      return (
        <span className="tw-inline-flex tw-items-center tw-gap-1.5">
          <Button
            onClick={() => redirectToLoginPage()}
            variant="link"
            className="tw-p-0 tw-font-semibold tw-text-[2rem]"
          >
            <>Log in</>
          </Button>
          to see if you’ve unlocked Priority Access 👀
        </span>
      );

    if (!isTieredUser)
      return 'You’re building towards Priority Access. Keep it up! 💪🔥';

    if (products.length === 0)
      return 'You’ve unlocked Priority Access. New items coming your way soon!';

    return 'You’ve unlocked Priority Access through your orders. Enjoy first dibs!';
  }, [
    isSpecialCategoryActive,
    isLoadingAuth,
    isLoadingProducts,
    isGuest,
    isTieredUser,
    products,
  ]);

  return (
    <Page
      content={
        <>
          <ProductCategoriesTabs
            className="lg:tw-hidden"
            collections={collections}
            categories={categories}
            specialProductCategories={specialProductCategories}
            isLoading={isLoadingUser && isLoadingAuth}
            isTieredUser={isTieredUser}
          />
          <Container className="tw-py-8 tw-min-h-screen">
            <HeaderSEO
              title="Yummy Bros - The Most Enjoyable Healthy Meal Prep in Singapore!"
              description="We’ve created healthy renditions of popular Asian cuisines by controlling the ingredients and portions that go into each dish."
              path="https://www.yummybros.com/products"
              image="https://yb-production.s3.ap-southeast-1.amazonaws.com/images/static/facebook-share-image-2020.jpeg"
              ogType="website"
            />
            <div className="tw-grid tw-grid-cols-5 md:tw-gap-8">
              <ProductCategoriesSidebar
                className="lg:tw-col-span-1 tw-hidden lg:tw-flex tw-sticky tw-top-56 tw-self-start"
                collections={collections}
                categories={categories}
                specialProductCategories={specialProductCategories}
                isTieredUser={isTieredUser}
                isLoading={isLoadingUser && isLoadingAuth}
              />
              <div className="tw-col-span-5 lg:tw-col-span-4 tw-flex tw-flex-col tw-gap-4 tw-h-full">
                <div className="tw-flex tw-flex-col tw-gap-8 md:tw-gap-12">
                  {specialProductTitle && (
                    <div className="tw-text-center tw-space-y-4">
                      <TextBalancer
                        as="h5"
                        className="tw-leading-snug tw-tracking-tight tw-mb-0"
                      >
                        {specialProductTitle}
                      </TextBalancer>
                      {/* <div className="tw-w-1/4 tw-h-1 tw-bg-primary-500 tw-mx-auto tw-rounded-full" /> */}
                    </div>
                  )}

                  {!isLoadingAuth && (
                    <ProductsGrid
                      isLoading={isLoadingProducts}
                      isLoadingMoreProducts={isLoadingMoreProducts}
                      products={products}
                      loadedCount={loadedCount}
                      onLoadMore={() => {
                        setIsLoadingMoreProducts(true);
                        setLoadedCount((prev) => prev + PAGE_SIZE);
                        setIsLoadingMoreProducts(false);
                      }}
                      showEmptyState={!isSpecialCategoryActive}
                    />
                  )}
                </div>
              </div>
            </div>
            <Drawer
              show={showProductFiltersDrawer}
              onHide={() => setShowProductFiltersDrawer(false)}
            >
              <TagsFilter
                // @ts-ignore
                value={tag_ids}
                onChange={(newValue) => {
                  setQueryParams({ ...query, tag_ids: newValue });
                }}
                onClear={() => {
                  const newQuery = { ...query };
                  delete newQuery.tag_ids;

                  setQueryParams({ ...newQuery });
                }}
              />
            </Drawer>
            <div className="tw-fixed tw-bottom-8 tw-right-6 sm:tw-right-32 md:tw-right-8 xl:tw-right-[26rem] tw-z-0">
              <div className="tw-flex tw-justify-end">
                <Button
                  color="secondary"
                  className="tw-w-16 tw-h-16 lg:tw-hidden lg:tw-w-16 lg:tw-h-16 tw-rounded-full tw-min-w-0 tw-shadow-lg tw-relative hover:tw-bg-secondary hover:tw-border-secondary"
                  onClick={() => setShowProductFiltersDrawer(true)}
                >
                  <i className="fa fa-sliders-h" />
                </Button>
              </div>
            </div>
          </Container>
        </>
      }
    />
  );
};

export default ProductsPage;

export const getServerSideProps: GetServerSideProps<ServerSideProps> = async (
  ctx,
) => {
  const session = await getSession(ctx);
  const accessToken = session?.user.accessToken;

  const authHeaders = accessToken
    ? { Authorization: `Bearer ${accessToken}` }
    : undefined;

  const { category_ids, collection_ids, tag_ids, search } =
    ctx.query ?? {};

  const productsQuery = {
    category_ids,
    collection_ids,
    tag_ids,
    search,
    sort_field: 'published_at',
  };

  let collections: CollectionMenu[] = [];

  const newLaunchesCollections =
    process.env.NEW_LAUNCHES_COLLECTION_ID;
  const hotSellerCollections = process.env.HOT_SELLERS_COLLECTION_ID;

  if (newLaunchesCollections)
    collections.push(JSON.parse(newLaunchesCollections));

  if (hotSellerCollections)
    collections.push(JSON.parse(hotSellerCollections));

  const {
    response: productsRes,
    error: productsError,
  } = await productsApi.getProducts({
    query: productsQuery,
    headers: authHeaders,
  });

  const {
    response: categoryRes,
    error: categoryError,
  } = await productCategoryApi.getCategoryById(category_ids);

  if (productsError || categoryError) {
    return { notFound: true };
  }

  const categories: ProductCategory[] = process.env.CATEGORIES
    ? JSON.parse(process.env.CATEGORIES)
    : [];

  const specialProductCategories: SpecialProductCategory[] = process
    .env.SPECIAL_PRODUCT_CATEGORIES
    ? JSON.parse(process.env.SPECIAL_PRODUCT_CATEGORIES)
    : [];

  const key = makeProductsCacheKey(productsQuery);

  return {
    props: {
      category: categoryRes ?? null,
      fallback: {
        products: {
          [key]: productsRes.data ?? [],
        },
      },
      collections,
      categories,
      specialProductCategories,
    },
  };
};
